// frontend/src/utils/api.js
// Axios client — injects Firebase ID token and unwraps the standard envelope.
//
// Every API call returns response.data.data (the raw rows array).
// The meta and schema fields are available if a consumer needs them:
//   const { data, meta, schema } = await apiRaw(...)
//
// Power BI / Looker / Tableau connect directly to the FastAPI endpoints
// and consume the full envelope — they don't use this file.

import axios from 'axios';
import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const client = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: { indexes: null },  // arrays → repeated params: key=a&key=b
});

// Inject Firebase auth token on every request
client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Unwrap envelope → returns raw rows array by default
const get = (url, params) =>
  client.get(url, { params }).then(r => r.data.data);


// ── Meta ──────────────────────────────────────────────────────────────────────
export const Meta = {
  months:  ()  => get('/api/v1/meta/months'),
  filters: ()  => get('/api/v1/meta/filters'),
};

// ── Overview ──────────────────────────────────────────────────────────────────
export const Overview = {
  kpis:  (p) => get('/api/v1/overview/kpis',  p),
  trend: (p) => get('/api/v1/overview/trend', p),
};

// ── Age ───────────────────────────────────────────────────────────────────────
// Response columns: month_id, category, session_type, age, [application],
//                   active_users, total_15min_sessions, total_15min_sessions
export const Age = {
  trend:    (p) => get('/api/v1/age/trend',    p),
  snapshot: (p) => get('/api/v1/age/snapshot', p),   // includes 'application' column
};

// ── Gender ────────────────────────────────────────────────────────────────────
// Response columns: month_id, category, session_type, gender, [application], ...
export const Gender = {
  trend:    (p) => get('/api/v1/gender/trend',    p),
  snapshot: (p) => get('/api/v1/gender/snapshot', p),
};

// ── Geography ─────────────────────────────────────────────────────────────────
// Response columns: month_id, category, session_type, geography, active_users, ...
// Pass limit=N for top N postcodes; omit or limit=0 for all 126
export const Geo = {
  snapshot:      (p) => get('/api/v1/geo/snapshot',       p),
  byApp:         (p) => get('/api/v1/geo/by-app',         p),
  trend:         (p) => get('/api/v1/geo/trend',           p),
  postcodeApps:  (p) => get('/api/v1/geo/postcode-apps',  p),
  postcodeTrend:     (p) => get('/api/v1/geo/postcode-trend',      p),
  postcodeAppsTrend: (p) => get('/api/v1/geo/postcode-apps-trend', p),
};

// ── Panel Size ────────────────────────────────────────────────────────────────
// overview → one row per (attribute, attribute_value): universe, avg_daily_uniques,
//            cat_users, category_share_pct
// trend    → one row per month: panel_size, avg_daily_uniques, cat_users
export const Panel = {
  overview:     (p) => get('/api/v1/panel/overview',      p),
  trend:        (p) => get('/api/v1/panel/trend',         p),
  demographics: (p) => get('/api/v1/panel/demographics',  p),
};

// ── Category Behaviour ────────────────────────────────────────────────────────
// Response columns: month_id, active_users, total_15min_sessions,
//                   total_15min_sessions, panel_size, penetration_pct
export const CategoryBehaviour = {
  trend: (p) => get('/api/v1/category/behaviour-trend', p),
  // Cross-category penetration comparison (all categories). Optional geo param
  // (postcode area) recomputes penetration within that region.
  // Response columns: category, users, penetration_pct [, panel_size | universe].
  penetrationCompare: (p) => get('/api/v1/category/penetration-compare', p),
  // Penetration responses share columns: segment, users, universe,
  // penetration_pct, users_total, panel_size, national_penetration_pct.
  // Brand variants add an 'application' column (one row per app × segment).
  penetrationByGeo:    (p) => get('/api/v1/category/penetration-by-geo',    p),
  penetrationByAge:    (p) => get('/api/v1/category/penetration-by-age',    p),
  penetrationByGender: (p) => get('/api/v1/category/penetration-by-gender', p),
  brandPenetrationByGeo:    (p) => get('/api/v1/brand/penetration-by-geo',    p),
  brandPenetrationByAge:    (p) => get('/api/v1/brand/penetration-by-age',    p),
  brandPenetrationByGender: (p) => get('/api/v1/brand/penetration-by-gender', p),
};

// ── Growth Opportunities ──────────────────────────────────────────────────────
// Response columns: segment, universe, cat_users, cat_penetration_pct,
//   brand_users, brand_penetration_pct, opportunity_users, opportunity_pct,
//   national_cat_pct, national_brand_pct,
//   users_total_cat, users_total_brand, panel_size, application
export const Growth = {
  opportunitiesByAge:    (p) => get('/api/v1/growth/opportunities-by-age',    p),
  opportunitiesByGender: (p) => get('/api/v1/growth/opportunities-by-gender', p),
  opportunitiesByGeo:    (p) => get('/api/v1/growth/opportunities-by-geo',    p),
};

// ── Intensity ─────────────────────────────────────────────────────────────────
// Response columns: month_id, category, session_type, intensity, [application], ...
export const Intensity = {
  trend:      (p) => get('/api/v1/intensity/trend',       p),
  snapshot:   (p) => get('/api/v1/intensity/snapshot',    p),
  brandShare: (p) => get('/api/v1/intensity/brand-share', p),
};

// ── Loyalty ───────────────────────────────────────────────────────────────────
// Response columns: month_id, category, session_type, app_loyalty, [application], ...
export const Loyalty = {
  trend:      (p) => get('/api/v1/loyalty/trend',       p),
  appTrend:   (p) => get('/api/v1/loyalty/app-trend',   p),
  snapshot:   (p) => get('/api/v1/loyalty/snapshot',    p),
  brandShare: (p) => get('/api/v1/loyalty/brand-share', p),
};

// ── Competition ───────────────────────────────────────────────────────────────
// share    → one row per application: application, active_users, share_pct, ...
// overlap  → one row per (application_x, application_y): overlap_pct, ...
export const Competition = {
  share:    (p) => get('/api/v1/competition/share',    p),
  overlap:  (p) => get('/api/v1/competition/overlap',  p),
  // month_id, application, pct_sessions_lost — full history
  sorTrend: (p) => get('/api/v1/competition/sor-trend', p),
};

// ── Social ────────────────────────────────────────────────────────────────────
// overlap  → filter by application= or channel= or both
// trend    → monthly time series, same optional filters
export const Social = {
  applications: (p) => get('/api/v1/social/applications', p),
  overlap:      (p) => get('/api/v1/social/overlap', p),
  trend:        (p) => get('/api/v1/social/trend',   p),
  // reach_pct = active_users / total_app_users; pop_reach_pct = active_users / panel_size
  reach:        (p) => get('/api/v1/social/reach',   p),
};

// ── Brand ─────────────────────────────────────────────────────────────────────
export const Brand = {
  doubleJeopardy: (month_id, session_type, window = '15min', category) =>
    get('/api/v1/brand/double-jeopardy', { month_id, session_type, window, category }),
  appTrend: (category, session_type, window) =>
    get('/api/v1/brand/app-trend', { category, session_type, window }),
};

// ── Football Affinity (Omni Cross Category / Sports) ────────────────────────────
export const Football = {
  apps:  ()               => get('/api/v1/football/apps'),
  pivot: (month_id, apps) => get('/api/v1/football/pivot', { month_id, apps }),
};

// ── Theme Parks Affinity (Omni Cross Category) ──────────────────────────────────
export const ThemePark = {
  apps:         ()                                  => get('/api/v1/theme-park/apps'),
  sessionTypes: ()                                  => get('/api/v1/theme-park/session-types'),
  pivot:        (month_id, apps, session_type)      => get('/api/v1/theme-park/pivot', { month_id, apps, session_type }),
};

// ── Merlin online behaviour (Pinpoint Intelligence) ─────────────────────────────
export const Merlin = {
  online: (brand, month = '') => get('/api/v1/merlin/online', { brand, month }),
};

// ── Social Media Affinity (Omni Cross Category) ─────────────────────────────────
export const SocialMedia = {
  brand:            (month_id = '')                          => get('/api/v1/social-media/brand', { month_id }),
  intensity:        (month_id = '', session_type = 'open_app') => get('/api/v1/social-media/intensity', { month_id, session_type }),
  intensityFilters: ()                                       => get('/api/v1/social-media/intensity-filters'),
};

// ── Chat ──────────────────────────────────────────────────────────────────────
export const Chat = {
  send: (message, history, pageContext = {}) =>
    client.post('/api/v1/chat', { message, history, page_context: pageContext }).then(r => r.data),
};

// ── Thailand ──────────────────────────────────────────────────────────────────
// Verticals: 'food' (daily/weekly) | 'ridehailing' (daily/weekly/monthly)
// Daily:   date, week, app_name, unique_visitors, total_sessions
// Weekly:  week, app_name, unique_visitors, total_sessions
// Monthly: month, month_name, app_name, unique_visitors, total_sessions
// Filters response: data[0] = { app_names:[], alphas:[], provinces:[], districts:[], weeks:[], months:[] }
export const Th = {
  daily:      (vertical, p) => get(`/api/v1/th/${vertical}/daily`,            p),
  weekly:     (vertical, p) => get(`/api/v1/th/${vertical}/weekly`,           p),
  weeklyGeo:  (vertical, p) => get(`/api/v1/th/${vertical}/weekly-province`,  p),
  monthly:    (vertical, p) => get(`/api/v1/th/${vertical}/monthly`,          p),
  filters:    (vertical)    => get('/api/v1/th/meta/filters', { vertical }),
};

// ── Admin ─────────────────────────────────────────────────────────────────────
export const Admin = {
  invalidateCache:    () => client.post('/api/v1/admin/invalidate-cache').then(r => r.data),
  cacheInfo:          () => get('/api/v1/admin/cache-info'),
  countryCategories:  () => get('/api/v1/admin/meta/country-categories'),
  // Access management (Eureka admins only)
  accessUsers:  ()      => get('/api/v1/admin/access-users'),
  grantAccess:  (body)  => client.post('/api/v1/admin/access-users', body).then(r => r.data.data?.[0]),
  revokeAccess: (email) => client.delete(`/api/v1/admin/access-users/${encodeURIComponent(email)}`).then(r => r.data.data?.[0]),
};

export default client;
